import { useState, useEffect, useRef, Fragment } from 'react';
import { useTheme } from '../../../context/theme';
import { useToast } from '../../../context/toast';
import { api } from '../../../utils/api';
import { renderIcon } from '../../../utils/icons';
import { Btn, Inp, Card, EmptyState } from '../../../components/ui';
import { SkeletonList } from '../../../components/ui/Skeletons';

export default function PurchaseOrdersTab({ siteId, site, user }) {
  const { theme, isDark } = useTheme();
  const { toast } = useToast();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [expandedBills, setExpandedBills] = useState([]);
  const [expandedCOs, setExpandedCOs] = useState([]);
  const [expandedAttachments, setExpandedAttachments] = useState([]);
  const [uploadingPO, setUploadingPO] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ vendor_id: '', description: '', phase: '', cost_category: 'Other', cost_code_id: '', amount: '', entity_id: '' });
  const [coForm, setCoForm] = useState({ reason: '', change_amount: '' });
  const [showCO, setShowCO] = useState(null);
  const [costCodes, setCostCodes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [editingPO, setEditingPO] = useState(null);
  const [editForm, setEditForm] = useState({});
  const isAdmin = user.role === 'admin';
  const canPO = isAdmin || user.permissions?.po_manager;
  const isViewer = user.role === 'viewer';
  const phases = ['Find It', 'Vet It', 'Buy It', 'Entitle It', 'Package & Sell'];
  const categories = ['Environmental', 'Geotechnical', 'Survey', 'Title', 'Legal', 'Engineering', 'Government Fees', 'Utility', 'Carry Costs', 'Closing Costs', 'Other'];

  const load = async () => {
    try {
      const [p, v, cc, ent] = await Promise.all([
        api.get('/api/sites/' + siteId + '/pos'),
        api.get('/api/sites/' + siteId + '/vendors'),
        api.get('/api/cost-codes'),
        api.get('/api/entities'),
      ]);
      setPos(p.purchaseOrders || []);
      setVendors((v.vendors || []).filter(vn => vn.w9_status === 'verified'));
      setCostCodes(cc.costCodes || []);
      setEntities(ent.entities || []);
      setForm(f => ({ ...f, entity_id: site?.entity_id ? String(site.entity_id) : '' }));
      setLoading(false);
    } catch (e) { setLoading(false); }
  };

  useEffect(() => { load(); }, [siteId]);

  const expandPO = async (poId) => {
    if (expanded === poId) { setExpanded(null); return; }
    setExpanded(poId);
    try {
      const [b, co, att] = await Promise.all([
        api.get('/api/pos/' + poId + '/bills'),
        api.get('/api/pos/' + poId + '/change-orders'),
        api.get('/api/pos/' + poId + '/attachments'),
      ]);
      setExpandedBills(b.bills || []);
      setExpandedCOs(co.changeOrders || []);
      setExpandedAttachments(att.attachments || []);
    } catch (e) { /* ignore */ }
  };

  const uploadAttachment = async (poId, file) => {
    setUploadingPO(poId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.upload('/api/pos/' + poId + '/attachments', fd);
      setExpandedAttachments(prev => [res.attachment, ...prev]);
      toast('File attached');
    } catch (e) { toast(e.message, 'error'); }
    setUploadingPO(null);
  };

  const deleteAttachment = async (poId, attId) => {
    try {
      await api.del('/api/pos/' + poId + '/attachments/' + attId);
      setExpandedAttachments(prev => prev.filter(a => a.id !== attId));
      toast('Attachment removed');
    } catch (e) { toast(e.message, 'error'); }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes > 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
    return Math.round(bytes / 1024) + ' KB';
  };

  const createPO = async () => {
    try {
      if (!form.vendor_id || !form.amount) return toast('Vendor and amount required', 'error');
      const payload = { ...form, amount: Number(form.amount) };
      if (payload.entity_id) payload.entity_id = Number(payload.entity_id);
      else delete payload.entity_id;
      await api.post('/api/sites/' + siteId + '/pos', payload);
      toast('PO submitted for approval');
      setShowForm(false);
      setForm({ vendor_id: '', description: '', phase: '', cost_category: 'Other', cost_code_id: '', amount: '', entity_id: site?.entity_id ? String(site.entity_id) : '' });
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const approvePO = async (id, action) => {
    try {
      const comments = action === 'reject' ? prompt('Rejection reason:') : '';
      if (action === 'reject' && !comments) return;
      await api.put('/api/pos/' + id + '/approve', { action, comments });
      toast('PO ' + action + 'd'); load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const issuePO = async (id) => {
    try { await api.put('/api/pos/' + id + '/issue', {}); toast('PO issued'); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const submitCO = async (poId) => {
    try {
      if (!coForm.reason || !coForm.change_amount) return toast('Reason and amount required', 'error');
      await api.post('/api/pos/' + poId + '/change-orders', { reason: coForm.reason, change_amount: Number(coForm.change_amount) });
      toast('Change order submitted');
      setShowCO(null);
      setCoForm({ reason: '', change_amount: '' });
      expandPO(poId);
    } catch (e) { toast(e.message, 'error'); }
  };

  const deletePO = async (id, poNumber) => {
    if (!confirm('Delete ' + poNumber + '? This cannot be undone.')) return;
    try { await api.del('/api/pos/' + id); toast('PO deleted'); setExpanded(null); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const startEditPO = (po) => {
    setEditingPO(po.id);
    setEditForm({ description: po.description || '', phase: po.phase || '', cost_code_id: po.cost_code_id ? String(po.cost_code_id) : '', amount: String(po.amount || '') });
  };

  const saveEditPO = async (id) => {
    try {
      const payload = { ...editForm, amount: editForm.amount ? Number(editForm.amount) : undefined, cost_code_id: editForm.cost_code_id ? Number(editForm.cost_code_id) : undefined };
      await api.put('/api/pos/' + id, payload);
      toast('PO updated');
      setEditingPO(null);
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const approveCO = async (id, action) => {
    try {
      const comments = action === 'reject' ? prompt('Rejection reason:') : '';
      if (action === 'reject' && !comments) return;
      await api.put('/api/change-orders/' + id + '/approve', { action, comments });
      toast('CO ' + action + 'd');
      if (expanded) expandPO(expanded);
    } catch (e) { toast(e.message, 'error'); }
  };

  const statusBadge = (s) => {
    const colors = { 'Draft': ['rgba(255,255,255,0.06)', 'rgba(255,255,255,0.58)'], 'Pending Approval': ['#92400e', '#fbbf24'], 'Approved': ['#dcfce7', '#4ADE80'], 'Issued': ['#dbeafe', '#2563eb'], 'Partially Billed': ['#ede9fe', '#7c3aed'], 'Fully Billed': ['#e0e7ff', '#4338ca'], 'Closed': ['#f3f4f6', '#9ca3af'] };
    const c = colors[s] || ['rgba(255,255,255,0.03)', 'rgba(255,255,255,0.58)'];
    return <span style={{ background: c[0], color: c[1], padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{s}</span>;
  };

  const selStyle = { padding: '8px 12px', background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: 8, color: theme.text, fontSize: 13 };

  if (loading) return <Card><SkeletonList count={4} /></Card>;

  const acctLink = (e) => { e.preventDefault(); window.history.pushState({ view: 'accounting' }, '', '/accounting?site_id=' + siteId); window.dispatchEvent(new PopStateEvent('popstate', { state: { view: 'accounting' } })); };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text, margin: 0 }}>
          Purchase Orders<span style={{ color: theme.textSecondary, fontSize: 12, marginLeft: 8 }}>({pos.length})</span>
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <a href={'/accounting?site_id=' + siteId} onClick={acctLink} style={{ fontSize: 12, color: theme.accent, textDecoration: 'none' }}>View in Accounting →</a>
          {!isViewer && <Btn sm primary onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : '+ Request PO'}</Btn>}
          <a href={'/api/sites/' + siteId + '/pos/export'} style={{ padding: '5px 12px', background: 'transparent', border: '1px solid ' + theme.border, borderRadius: 8, color: theme.textSecondary, fontSize: 12, textDecoration: 'none' }}>{renderIcon('download', 12)} CSV</a>
        </div>
      </div>

      {showForm && (
        <div style={{ background: isDark ? '#1e1e24' : '#f8f9fa', border: '1px solid ' + theme.border, borderRadius: 10, padding: 16, marginBottom: 16 }}>
          {vendors.length === 0
            ? <p style={{ color: '#EF4444', fontSize: 13 }}>No vendors with verified W-9. Add and verify a vendor first.</p>
            : <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <select value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })} style={selStyle}>
                  <option value="">Select Vendor *</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.company_name}</option>)}
                </select>
                <Inp type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="Amount ($) *" />
                <select value={form.phase} onChange={e => setForm({ ...form, phase: e.target.value })} style={selStyle}>
                  <option value="">Phase</option>
                  {phases.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={form.cost_code_id} onChange={e => setForm({ ...form, cost_code_id: e.target.value })} style={selStyle}>
                  <option value="">Cost Code</option>
                  {costCodes.map(c => <option key={c.id} value={c.id}>{c.code} — {c.label}</option>)}
                </select>
                <select value={form.entity_id} onChange={e => setForm({ ...form, entity_id: e.target.value })} style={selStyle}>
                  <option value="">Entity (auto from site)</option>
                  {entities.map(en => <option key={en.id} value={en.id}>{en.short_name || en.name}</option>)}
                </select>
              </div>
              <Inp value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description of services" style={{ marginTop: 10, width: '100%' }} />
              <Btn sm primary onClick={createPO} style={{ marginTop: 10 }}>Submit PO Request</Btn>
            </>
          }
        </div>
      )}

      {pos.length === 0 && !showForm && <EmptyState icon="clipboard-list" title="No purchase orders" description="Request a PO to start tracking commitments" action={!isViewer ? '+ Request PO' : undefined} onAction={!isViewer ? () => setShowForm(true) : undefined} />}

      {pos.map(po => (
        <div key={po.id} style={{ border: '1px solid ' + theme.border, borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
          <div className="hover-row" onClick={() => expandPO(po.id)} style={{ display: 'grid', gridTemplateColumns: '100px 2fr 1.5fr 1fr 1fr 1fr 1fr', padding: '10px 12px', fontSize: 13, alignItems: 'center', cursor: 'pointer', background: expanded === po.id ? (isDark ? '#1e1e24' : '#f0f4ff') : 'transparent', gap: 8 }}>
            <span style={{ fontWeight: 700, color: '#4ADE80', fontFamily: 'monospace' }}>{po.po_number}</span>
            <div>
              <div style={{ fontWeight: 600, color: theme.text }}>{po.vendor_name}</div>
              <div style={{ fontSize: 11, color: theme.textSecondary }}>{po.description?.substring(0, 50) || (po.phase || '')}</div>
            </div>
            <span style={{ fontSize: 11 }}>
              {po.phase && <span style={{ background: isDark ? 'rgba(255,255,255,0.10)' : '#e2e8f0', padding: '2px 6px', borderRadius: 4, marginRight: 4 }}>{po.phase}</span>}
              {po.cost_code_label || po.cost_category}
            </span>
            <span style={{ textAlign: 'right', fontWeight: 600, color: theme.text }}>${Number(po.amount).toLocaleString()}</span>
            <span style={{ textAlign: 'right', color: '#4ADE80' }}>${Number(po.billed_to_date || 0).toLocaleString()}</span>
            <span style={{ textAlign: 'right', color: theme.textSecondary }}>${(Number(po.amount) - Number(po.billed_to_date || 0) + Number(po.change_order_total || 0)).toLocaleString()}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
              {statusBadge(po.status)}
              {po.status === 'Pending Approval' && canPO && (
                <>
                  <button onClick={e => { e.stopPropagation(); approvePO(po.id, 'approve'); }} style={{ background: '#4ADE80', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>✓</button>
                  <button onClick={e => { e.stopPropagation(); approvePO(po.id, 'reject'); }} style={{ background: '#EF4444', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>✗</button>
                </>
              )}
              {po.status === 'Approved' && canPO && <button onClick={e => { e.stopPropagation(); issuePO(po.id); }} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Issue</button>}
            </div>
          </div>

          {expanded === po.id && (
            <div style={{ padding: '12px', borderTop: '1px solid ' + theme.border, background: isDark ? '#16161a' : '#f8f9fa' }}>
              {/* PO Manager actions bar */}
              {canPO && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid ' + theme.border }}>
                  <button onClick={() => editingPO === po.id ? setEditingPO(null) : startEditPO(po)} style={{ fontSize: 11, fontWeight: 600, color: theme.accent, background: 'none', border: '1px solid ' + theme.accent + '80', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>{editingPO === po.id ? 'Cancel Edit' : 'Edit PO'}</button>
                  <button onClick={() => deletePO(po.id, po.po_number)} style={{ fontSize: 11, fontWeight: 600, color: '#EF4444', background: 'none', border: '1px solid #ef444480', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>Delete PO</button>
                </div>
              )}

              {/* Edit form */}
              {editingPO === po.id && (
                <div style={{ background: isDark ? '#1e1e24' : '#f0f4ff', border: '1px solid ' + theme.border, borderRadius: 8, padding: 12, marginBottom: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <Inp type="number" value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })} placeholder="Amount ($)" />
                    <select value={editForm.phase} onChange={e => setEditForm({ ...editForm, phase: e.target.value })} style={selStyle}>
                      <option value="">Phase</option>
                      {phases.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <select value={editForm.cost_code_id} onChange={e => setEditForm({ ...editForm, cost_code_id: e.target.value })} style={selStyle}>
                      <option value="">Cost Code</option>
                      {costCodes.map(c => <option key={c.id} value={c.id}>{c.code} — {c.label}</option>)}
                    </select>
                  </div>
                  <Inp value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" style={{ marginTop: 8, width: '100%' }} />
                  <Btn sm primary onClick={() => saveEditPO(po.id)} style={{ marginTop: 8 }}>Save Changes</Btn>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: theme.textSecondary }}>BILLS FOR THIS PO</span>
                {!isViewer && !['Closed', 'Draft', 'Pending Approval'].includes(po.status) && (
                  <button onClick={() => setShowCO(showCO === po.id ? null : po.id)} style={{ fontSize: 11, color: '#F59E0B', background: 'none', border: '1px solid #f59e0b', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>
                    {showCO === po.id ? 'Cancel' : '+ Change Order'}
                  </button>
                )}
              </div>

              {showCO === po.id && (
                <div style={{ padding: 10, background: theme.bgCard, border: '1px solid #f59e0b40', borderRadius: 8, marginBottom: 8 }}>
                  <Inp value={coForm.reason} onChange={e => setCoForm({ ...coForm, reason: e.target.value })} placeholder="Reason for change order" />
                  <Inp type="number" value={coForm.change_amount} onChange={e => setCoForm({ ...coForm, change_amount: e.target.value })} placeholder="Change amount (+/-)" style={{ marginTop: 6 }} />
                  <Btn sm onClick={() => submitCO(po.id)} style={{ marginTop: 6, background: '#F59E0B', color: '#fff' }}>Submit Change Order</Btn>
                </div>
              )}

              {expandedCOs.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#F59E0B', marginBottom: 4 }}>CHANGE ORDERS</div>
                  {expandedCOs.map(co => (
                    <div key={co.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: theme.bgCard, borderRadius: 6, marginBottom: 4, border: '1px solid ' + theme.border, fontSize: 12 }}>
                      <span>CO #{co.co_number} — {co.reason}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 700, color: Number(co.change_amount) >= 0 ? '#4ADE80' : '#EF4444' }}>{(Number(co.change_amount) >= 0 ? '+$' : '-$') + Math.abs(co.change_amount).toLocaleString()}</span>
                        <span style={{ background: co.status === 'Approved' ? '#dcfce7' : co.status === 'Rejected' ? '#fecaca' : '#fef3c7', color: co.status === 'Approved' ? '#4ADE80' : co.status === 'Rejected' ? '#EF4444' : '#F59E0B', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{co.status}</span>
                        {co.status === 'Pending Approval' && canPO && (
                          <>
                            <button onClick={() => approveCO(co.id, 'approve')} style={{ background: '#4ADE80', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer' }}>✓</button>
                            <button onClick={() => approveCO(co.id, 'reject')} style={{ background: '#EF4444', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer' }}>✗</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {expandedBills.length === 0
                ? <p style={{ fontSize: 12, color: theme.textSecondary, fontStyle: 'italic' }}>No bills submitted against this PO</p>
                : expandedBills.map(b => (
                  <div key={b.id} className="hover-row" style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr 1fr', padding: '6px 8px', fontSize: 12, borderBottom: '1px solid ' + theme.border, cursor: 'default' }}>
                    <span style={{ fontFamily: 'monospace', color: '#4ADE80' }}>{b.bill_number}</span>
                    <span>{b.invoice_number || '—'}</span>
                    <span>${Number(b.amount).toLocaleString()}</span>
                    <span>{statusBadge(b.status)}</span>
                    <span>{b.paid_date ? new Date(b.paid_date).toLocaleDateString() : '—'}</span>
                  </div>
                ))
              }

              {/* Attachments */}
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: theme.textSecondary }}>
                    ATTACHMENTS {expandedAttachments.length > 0 && <span style={{ fontWeight: 400 }}>({expandedAttachments.length})</span>}
                  </span>
                  {!isViewer && (
                    <>
                      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={e => {
                        if (e.target.files[0]) uploadAttachment(po.id, e.target.files[0]);
                        e.target.value = '';
                      }} />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPO === po.id}
                        style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6,
                          border: '1px solid ' + theme.border, cursor: 'pointer',
                          background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                          color: theme.textSecondary, opacity: uploadingPO === po.id ? 0.5 : 1,
                        }}
                      >
                        {uploadingPO === po.id ? 'Uploading...' : renderIcon('paperclip', 11) + ' Attach File'}
                      </button>
                    </>
                  )}
                </div>
                {expandedAttachments.length === 0 ? (
                  <p style={{ fontSize: 12, color: theme.textSecondary, fontStyle: 'italic' }}>No files attached</p>
                ) : expandedAttachments.map(att => (
                  <div key={att.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                    background: theme.bgCard, borderRadius: 6, marginBottom: 4,
                    border: '1px solid ' + theme.border,
                  }}>
                    {renderIcon('file', 14, theme.textMuted)}
                    <a href={att.blob_url} target="_blank" rel="noopener noreferrer" style={{
                      flex: 1, fontSize: 12, fontWeight: 500, color: theme.accent,
                      textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                       onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                      {att.original_name || att.file_name}
                    </a>
                    <span style={{ fontSize: 10, color: theme.textMuted, flexShrink: 0 }}>
                      {fmtSize(att.file_size)}
                      {att.uploader_name ? ' · ' + att.uploader_name : ''}
                    </span>
                    {(isAdmin || att.uploaded_by === user.id) && (
                      <button onClick={() => deleteAttachment(po.id, att.id)} title="Remove" style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: theme.textSecondary,
                        fontSize: 13, padding: '2px 4px', opacity: 0.6,
                      }} onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#EF4444'; }}
                         onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = theme.textSecondary; }}>
                        {renderIcon('trash-2', 13)}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </Card>
  );
}
